// ppt-gen.ppt.service.ts
import { Injectable } from "@angular/core";
import pptxgen from "pptxgenjs";
import { saveAs } from 'file-saver';
import { PptGenCommonService } from './ppt-gen.common.service';

@Injectable({
    providedIn: 'root'
})
export class PptGenService {

    constructor(private commonService: PptGenCommonService) { }

    /**
     * Generates a PPTX file for a single vendor submission.
     * @param submissionId The ID of the submission.
     * @param vendorName The name of the vendor.
     * @param sectionsData Data for the sections, including questions.
     * @param answersData Raw answer data from the API.
     */
    async generateVendorSubmissionPPT(
        submissionId: number,
        vendorName: any,
        sectionsData: any[],
        answersData: any[]
    ) {
        console.log("Generating PPT...");
        let pres = new pptxgen();

        // --- Global Slide Layout Settings ---
        const SLIDE_MARGIN_X = 0.5;
        const SLIDE_MARGIN_Y = 0.5;
        const MAX_USABLE_SLIDE_HEIGHT = 6.5; // Adjust based on your actual testing
        const QUESTION_FONT_SIZE = 12;
        const ANSWER_FONT_SIZE = 11;
        const LINE_HEIGHT_FACTOR = 0.18;
        const SPACE_AFTER_QUESTION = 0.1;
        const SPACE_AFTER_ANSWER = 0.3;

        // --- Title Slide ---
        let titleSlide = pres.addSlide();
        titleSlide.addText('Vendor Submission Details', { x: 1, y: 1, fontSize: 36, bold: true, color: "005691" });
        titleSlide.addText(`Submission ID: ${submissionId}`, { x: 1, y: 2, fontSize: 20, color: "333333" });
        titleSlide.addText(`Vendor: ${vendorName || 'N/A'}`, { x: 1, y: 2.5, fontSize: 20, color: "333333" });
        titleSlide.addText(`Date Generated: ${new Date().toLocaleDateString()}`, { x: 1, y: 3, fontSize: 16, color: "666666" });

        // --- Loop through each section ---
        for (const section of sectionsData) {
            let slide = pres.addSlide();
            let yPos = SLIDE_MARGIN_Y;

            // Section Title
            slide.addText(section.section_title, {
                x: SLIDE_MARGIN_X,
                y: yPos,
                fontSize: 28,
                bold: true,
                color: "005691",
                w: 9,
                h: 0.5
            });
            yPos += 0.7;

            // Section Description (if any)
            if (section.description) {
                slide.addText(section.description, {
                    x: SLIDE_MARGIN_X + 0.1,
                    y: yPos,
                    fontSize: 14,
                    color: "808080",
                    w: 9 - 0.2,
                    h: 0.4
                });
                yPos += 0.5;
            }

            // --- Loop through each question in the section ---
            for (const question of section.questions) {
                const answer = this.commonService.getAnswerForQuestion(question.id, answersData);

                const questionText = `${question.question_text}:`;
                const estimatedQuestionLines = Math.ceil(questionText.length / 80) || 1;
                let estimatedAnswerLines = 1;
                let answerText = 'No answer provided.';
                let answerColor = "333333";

                if (answer) {
                    switch (question.question_type) {
                        case 'TEXT_SHORT':
                            answerText = answer.answer_text_short || 'N/A';
                            break;
                        case 'TEXT_LONG':
                            answerText = answer.answer_text_long || 'N/A';
                            break;
                        case 'NUMBER':
                            answerText = (answer.answer_number !== null && answer.answer_number !== undefined) ? String(answer.answer_number) : 'N/A';
                            break;
                        case 'CURRENCY':
                            answerText = (answer.answer_number !== null && answer.answer_number !== undefined) ? `$${answer.answer_number.toFixed(2)}` : 'N/A';
                            break;
                        case 'BOOLEAN_YES_NO':
                            answerText = answer.answer_boolean ? 'Yes' : 'No';
                            break;
                        case 'DATE':
                            answerText = answer.answer_date ? new Date(answer.answer_date).toLocaleDateString() : 'N/A';
                            break;
                        case 'FILE_UPLOAD':
                            if (answer.answer_file_references && answer.answer_file_references.length > 0) {
                                answerText = 'Attached Files:\n' + answer.answer_file_references.map((fileRef: any) =>
                                    `- ${fileRef.file_name || 'Unnamed File'} (${fileRef.file_url})`
                                ).join('\n');
                            } else {
                                answerText = 'No files attached.';
                            }
                            break;
                        case 'MULTIPLE_CHOICE_SINGLE':
                        case 'DROPDOWN':
                            answerText = this.commonService.getChoiceText(question.choices, answer.answer_selected_choice_ids);
                            break;
                        case 'MULTIPLE_CHOICE_MULTI':
                            if (answer.answer_selected_choice_ids && answer.answer_selected_choice_ids.length > 0) {
                                answerText = 'Selected Choices:\n' + answer.answer_selected_choice_ids.map((choiceId: any) =>
                                    `- ${this.commonService.getChoiceText(question.choices, choiceId)}`
                                ).join('\n');
                            } else {
                                answerText = 'No choices selected.';
                            }
                            break;
                        default:
                            answerText = `Unsupported or unhandled question type: ${question.question_type}`;
                            answerColor = "FF8C00";
                            break;
                    }
                } else {
                    answerColor = "FF0000";
                }

                estimatedAnswerLines = answerText.split('\n').length;
                const charsPerLineForAnswer = 90;
                if (estimatedAnswerLines === 1 && answerText.length > charsPerLineForAnswer) {
                    estimatedAnswerLines = Math.ceil(answerText.length / charsPerLineForAnswer);
                }

                const estimatedContentHeight =
                    (estimatedQuestionLines * LINE_HEIGHT_FACTOR * (QUESTION_FONT_SIZE / ANSWER_FONT_SIZE)) +
                    SPACE_AFTER_QUESTION +
                    (estimatedAnswerLines * LINE_HEIGHT_FACTOR) +
                    SPACE_AFTER_ANSWER;

                if (yPos + estimatedContentHeight > MAX_USABLE_SLIDE_HEIGHT) {
                    slide = pres.addSlide();
                    yPos = SLIDE_MARGIN_Y;
                    slide.addText(`(Continued) ${section.section_title}`, {
                        x: SLIDE_MARGIN_X,
                        y: SLIDE_MARGIN_Y,
                        fontSize: 24,
                        bold: true,
                        color: "808080",
                        w: 9,
                        h: 0.4
                    });
                    yPos += 0.7;
                }

                slide.addText(questionText, {
                    x: SLIDE_MARGIN_X + 0.2,
                    y: yPos,
                    fontSize: QUESTION_FONT_SIZE,
                    bold: true,
                    w: 9 - 0.4,
                    h: estimatedQuestionLines * LINE_HEIGHT_FACTOR * (QUESTION_FONT_SIZE / ANSWER_FONT_SIZE),
                    autoFit: true
                });
                yPos += (estimatedQuestionLines * LINE_HEIGHT_FACTOR * (QUESTION_FONT_SIZE / ANSWER_FONT_SIZE)) + SPACE_AFTER_QUESTION;

                slide.addText(answerText, {
                    x: SLIDE_MARGIN_X + 0.4,
                    y: yPos,
                    fontSize: ANSWER_FONT_SIZE,
                    color: answerColor,
                    w: 9 - 0.6,
                    h: estimatedAnswerLines * LINE_HEIGHT_FACTOR,
                    autoFit: true
                });
                yPos += (estimatedAnswerLines * LINE_HEIGHT_FACTOR) + SPACE_AFTER_ANSWER;
            }
        }

        // --- Concluding Slide ---
        let finalSlide = pres.addSlide();
        const finalSlideCenterY = 3.2;

        finalSlide.addText('End of Vendor Submission Report', {
            x: 0,
            w: '100%',
            y: finalSlideCenterY,
            align: 'center',
            fontSize: 32,
            bold: true,
            color: "005691"
        });

        finalSlide.addText('Thank you for your submission.', {
            x: 0,
            w: '100%',
            y: finalSlideCenterY + 1.0,
            align: 'center',
            fontSize: 18,
            color: "666666"
        });

        pres.writeFile({ fileName: `Vendor_Submission_${submissionId}.pptx` });
        console.log("PPT Generation Complete.");
    }
}
